import React from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { CheckCircle, Buildings, WifiHigh, Wrench, Megaphone, ChartBar, Printer, ArrowLeft } from "@phosphor-icons/react";

// One-page sales pitch for student-residence landlords. Designed to print as
// a clean A4 PDF (toolbar > Print > Save as PDF) and also live as a shareable
// URL that the founder can send to landlords ahead of a meeting.
const FRONTEND = typeof window !== "undefined" ? window.location.origin : "https://curbcrew.co.za";

export default function PitchResidence() {
  return (
    <div className="bg-white text-black">
      {/* No-print toolbar */}
      <div className="no-print sticky top-0 z-10 bg-white border-b border-black">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
          <Link to="/" className="label-tag inline-flex items-center gap-2" data-testid="pitch-back">
            <ArrowLeft size={14} weight="bold" /> Back to site
          </Link>
          <div className="label-tag text-neutral-600 hidden sm:block">
            Pitch deck for student residence landlords · 1-page sales sheet
          </div>
          <button
            onClick={() => window.print()}
            className="bg-[color:var(--cc-yellow)] label-tag px-5 py-2 border-2 border-black brutal-btn flex items-center gap-2"
            data-testid="pitch-print"
          >
            <Printer size={14} weight="bold" /> Print / Save as PDF
          </button>
        </div>
      </div>

      {/* The actual pitch — one printable A4 page */}
      <article className="max-w-4xl mx-auto px-8 py-10 print:py-0 print:px-6">
        {/* Header */}
        <header className="border-b-2 border-black pb-5 flex items-start justify-between gap-6">
          <div>
            <div className="inline-block bg-black text-[color:var(--cc-yellow)] px-3 py-1 label-tag mb-3">
              CURB CREW · STUDENT RES
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none">
              Stop paying for transport<br />you can't verify.
            </h1>
            <p className="mt-4 text-base text-neutral-800 max-w-2xl">
              A simple NFC + QR platform built for student residences. Count boardings,
              track maintenance, give your students a noticeboard — all in one place,
              priced per bed.
            </p>
          </div>
          <div className="flex-shrink-0 bg-white border-2 border-black p-3 hidden sm:block">
            <QRCodeSVG value={`${FRONTEND}/pitch/residence`} size={92} level="H" includeMargin={false} />
            <div className="label-tag text-center mt-1 text-[9px]">SCAN TO SHARE</div>
          </div>
        </header>

        {/* The 4 problems */}
        <section className="mt-8">
          <div className="label-tag text-neutral-500 mb-3">The 4 daily headaches at your res</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PainCard
              icon={<WifiHigh size={20} weight="bold" />}
              title="You pay for transport you can't audit"
              body="University bills you R150-R300 per student per month for shuttle access — even when half of them never board. You have no way to prove it."
            />
            <PainCard
              icon={<Wrench size={20} weight="bold" />}
              title="Maintenance complaints lost in WhatsApp"
              body="A geyser leaks at 11pm. Student WhatsApps you. You forget. Next morning the entire ceiling is wet. Standard SA res reality."
            />
            <PainCard
              icon={<Megaphone size={20} weight="bold" />}
              title="Students need a place to buy + sell"
              body="Old textbooks, mini-fridges, lift requests, item-for-sale ads — they spam your WhatsApp group instead. You spend half a day moderating."
            />
            <PainCard
              icon={<ChartBar size={20} weight="bold" />}
              title="Local shops want to reach your students"
              body="The cash store, takeaway, laundromat all want to advertise to your residents. You have no clean way to offer them paid placement."
            />
          </div>
        </section>

        {/* The solution */}
        <section className="mt-8 bg-[color:var(--cc-yellow)] border-2 border-black p-6">
          <div className="flex items-start gap-4">
            <Buildings size={32} weight="bold" className="flex-shrink-0" />
            <div>
              <h2 className="font-display font-black text-2xl tracking-tight">One NFC card per student. One dashboard for you.</h2>
              <p className="mt-2 text-sm leading-relaxed">
                Each student gets a Curb Crew NFC + QR card at orientation. They tap it on the shuttle reader
                every time they board. You see a verified daily log — who used the transport, how often, by route.
                Same card hosts their maintenance reports, their noticeboard listings, and your sponsored
                local-shop ads. Built for South African res life. Works offline. Survives Wi-Fi outages.
              </p>
            </div>
          </div>
        </section>

        {/* What you get */}
        <section className="mt-8">
          <div className="label-tag text-neutral-500 mb-3">What's included</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <Bullet>NFC card per resident (lifetime, replace if lost)</Bullet>
            <Bullet>Tap-on / tap-off shuttle reader at your gate</Bullet>
            <Bullet>Live boarding log: who, when, which route</Bullet>
            <Bullet>Monthly transport audit report (PDF + CSV)</Bullet>
            <Bullet>Asset stickers on geysers, gates, pumps, alarms</Bullet>
            <Bullet>Fault reports with photo + status workflow</Bullet>
            <Bullet>Per-residence student noticeboard</Bullet>
            <Bullet>Buy/sell, lift requests, textbook ads</Bullet>
            <Bullet>Paid ad slots for local shops (revenue share)</Bullet>
            <Bullet>Email + WhatsApp notifications to you</Bullet>
            <Bullet>POPIA-compliant (each student opts in)</Bullet>
            <Bullet>1-year card replacement guarantee</Bullet>
          </div>
        </section>

        {/* Pricing */}
        <section className="mt-8 border-2 border-black">
          <div className="bg-black text-white px-6 py-3 font-display font-black text-lg tracking-tight">
            Simple per-bed pricing
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3">
            <PriceCol title="Setup" price="R3 500" sub="once-off">
              NFC reader hardware, installation, 1-hour staff training.
            </PriceCol>
            <PriceCol title="Per student" price="R7" sub="per bed / month" highlight>
              Includes the NFC card, lifetime access to the platform, all features above.
            </PriceCol>
            <PriceCol title="Ad share" price="50/50" sub="local shops">
              Half of every local-shop ad placed on your res board comes to you.
            </PriceCol>
          </div>
          <div className="px-6 py-3 text-xs text-neutral-700 border-t border-black">
            <strong>Example:</strong> 200-bed residence = R1,400/month for the platform. If you save just one student's
            unjustified transport bill (R200), the system pays for itself with 7 students of room to spare.
          </div>
        </section>

        {/* CTA */}
        <section className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-6 border-t-2 border-black">
          <div>
            <div className="font-display font-black text-xl tracking-tight">Want a 20-minute demo at your res?</div>
            <div className="text-sm text-neutral-700 mt-1">
              No-obligation site visit. We bring a reader, swipe a few demo cards, walk you through the dashboard.
            </div>
          </div>
          <div className="text-right">
            <div className="label-tag text-neutral-500">Reach out</div>
            <div className="font-mono-cc text-sm font-bold mt-0.5">altgasstove@gmail.com</div>
            <div className="font-mono-cc text-sm">curbcrew.co.za</div>
          </div>
        </section>

        {/* Footer */}
        <footer className="mt-6 text-[10px] text-neutral-500 tracking-wider uppercase">
          Curb Crew · A South African smart-card platform · Cape Town · POPIA-compliant
        </footer>
      </article>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4; margin: 12mm; }
          html, body { background: white; }
          article { padding: 0 !important; max-width: 100% !important; }
        }
      `}</style>
    </div>
  );
}

function PainCard({ icon, title, body }) {
  return (
    <div className="border border-black p-4">
      <div className="flex items-center gap-2">
        <div className="bg-black text-[color:var(--cc-yellow)] p-1.5">{icon}</div>
        <div className="font-display font-black text-base tracking-tight">{title}</div>
      </div>
      <p className="mt-2 text-xs text-neutral-700 leading-snug">{body}</p>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <div className="flex items-start gap-2 py-1">
      <CheckCircle size={14} weight="bold" className="flex-shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  );
}

function PriceCol({ title, price, sub, highlight, children }) {
  return (
    <div className={`p-5 ${highlight ? "bg-[color:var(--cc-yellow)]" : "bg-white"} ${highlight ? "border-x-2" : ""} border-black`}>
      <div className="label-tag text-neutral-700">{title}</div>
      <div className="font-display font-black text-3xl tracking-tighter mt-1">{price}</div>
      <div className="label-tag text-neutral-700">{sub}</div>
      <p className="mt-3 text-xs text-neutral-800 leading-snug">{children}</p>
    </div>
  );
}
